import { Layout } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Shuffle, Award } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import listsHero from "@/assets/lists-hero.jpg";

const ListShuffler = () => {
  const [input, setInput] = useState("");
  const [shuffled, setShuffled] = useState<string[]>([]);

  const shuffle = () => {
    const items = input.split("\n").filter((item) => item.trim());
    if (items.length < 2) {
      toast.error("Please enter at least 2 items");
      return;
    }

    const array = new Uint32Array(items.length);
    crypto.getRandomValues(array);
    const indexed = items.map((item, i) => ({ item, sort: array[i] }));
    indexed.sort((a, b) => a.sort - b.sort);
    
    setShuffled(indexed.map((x) => x.item));
    toast.success("List shuffled!");
  };

  return (
    <Card className="p-6 shadow-[var(--shadow-medium)]">
      <div className="flex items-center gap-2 mb-6">
        <Shuffle className="w-6 h-6 text-primary" />
        <h2 className="text-2xl font-bold">List Shuffler</h2>
      </div>

      <div className="mb-4">
        <Label htmlFor="list-input">Enter items (one per line)</Label>
        <Textarea
          id="list-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Apple&#10;Banana&#10;Cherry&#10;Date&#10;Elderberry"
          rows={8}
          className="mt-1 font-mono"
        />
      </div>

      <Button onClick={shuffle} className="w-full mb-4">
        Shuffle List
      </Button>

      {shuffled.length > 0 && (
        <div className="bg-secondary rounded-lg p-4">
          <p className="text-sm text-muted-foreground mb-2">Shuffled Results:</p>
          <ol className="list-decimal list-inside space-y-1 font-mono text-foreground">
            {shuffled.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ol>
        </div>
      )}
    </Card>
  );
};

const RandomPicker = () => {
  const [input, setInput] = useState("");
  const [winner, setWinner] = useState<string>("");

  const pick = () => {
    const items = input.split("\n").filter((item) => item.trim());
    if (items.length < 1) {
      toast.error("Please enter at least 1 item");
      return;
    }

    const array = new Uint32Array(1);
    crypto.getRandomValues(array);
    const index = array[0] % items.length;
    
    setWinner(items[index]);
    toast.success("Winner selected!");
  };

  return (
    <Card className="p-6 shadow-[var(--shadow-medium)]">
      <div className="flex items-center gap-2 mb-6">
        <Award className="w-6 h-6 text-accent" />
        <h2 className="text-2xl font-bold">Random Picker</h2>
      </div>

      <div className="mb-4">
        <Label htmlFor="picker-input">Enter items (one per line)</Label>
        <Textarea
          id="picker-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Alice&#10;Bob&#10;Charlie&#10;Diana&#10;Eve"
          rows={8}
          className="mt-1 font-mono"
        />
      </div>

      <Button onClick={pick} className="w-full mb-4">
        Pick Random Winner
      </Button>

      {winner && (
        <div className="bg-gradient-to-r from-accent to-accent/80 rounded-lg p-8 text-center">
          <p className="text-sm text-accent-foreground/80 mb-2">Winner:</p>
          <p className="text-3xl font-bold text-accent-foreground">{winner}</p>
        </div>
      )}
    </Card>
  );
};

const Lists = () => {
  return (
    <Layout>
      <SEO 
        title="List Shuffler & Random Picker - Randomize Lists"
        description="Shuffle lists and pick random winners with our cryptographically secure list randomizer. Perfect for raffles, giveaways, and random selections."
        canonical="https://numbers-generator.com/lists"
        keywords="list shuffler, random picker, randomize list, raffle picker, giveaway winner, random selection"
      />
      <div className="container mx-auto px-4 py-12">
        <header className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            RandomGen Numbers Generator - Lists & Picker Tools
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Shuffle lists, pick random winners, and randomize any sequence
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-6xl mx-auto">
          <ListShuffler />
          <RandomPicker />
        </div>

        {/* Hero Image at Bottom */}
        <div className="mt-12 rounded-2xl overflow-hidden shadow-2xl max-w-5xl mx-auto">
          <img 
            src={listsHero} 
            alt="List shuffling and random winner selection tool showing raffle drawings, winner trophy, and shuffle icons for fair random picking"
            className="w-full h-auto object-cover"
            loading="lazy"
          />
        </div>
      </div>
    </Layout>
  );
};

export default Lists;
